import { useEffect, useMemo, useRef, useState } from "react";
import { ModalCloseButton } from "../components/ModalCloseButton";
import { api } from "../lib/api";
import { useRegisterViewRefresh, useUnsavedChangesWarning } from "../lib/viewRefresh";

type RevertMode = "single" | "to_point";
const PAGE_SIZE = 50;

function useDebounced(value: string, ms = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

function formatLocation(row: any, side: "from" | "to"): string {
  const locationType = side === "from" ? row.fromLocationType : row.toLocationType;
  const apparatusName = side === "from" ? row.fromApparatusName : row.toApparatusName;
  const bagName = side === "from" ? row.fromBagName : row.toBagName;
  const stockLotId = side === "from" ? row.fromStockLotId : row.toStockLotId;

  if (locationType === "stock_room") {
    return stockLotId ? `Stock (${stockLotId})` : "Stock";
  }
  if (locationType === "bag") {
    const parts = [apparatusName, bagName].filter(Boolean);
    return parts.length > 0 ? parts.join(" / ") : "Apparatus";
  }
  return "—";
}

function formatQty(row: any): string {
  if (row.oldQuantity == null && row.newQuantity == null && row.quantityDelta == null) return "—";
  const from = row.oldQuantity == null ? "—" : String(row.oldQuantity);
  const to = row.newQuantity == null ? "—" : String(row.newQuantity);
  const delta = row.quantityDelta == null ? "" : ` (${row.quantityDelta > 0 ? "+" : ""}${row.quantityDelta})`;
  return `${from} -> ${to}${delta}`;
}

function formatBin(row: any): string {
  if (row.revertMetadataVersion == null && row.oldBinLabel == null && row.newBinLabel == null) return "—";
  const from = row.oldBinLabel?.trim() || "—";
  const to = row.newBinLabel?.trim() || "—";
  return from === to ? from : `${from} -> ${to}`;
}

export function ChangeLogScreen({
  enteredName,
  rememberName
}: {
  enteredName: string;
  rememberName: (name: string) => void;
}) {
  const [q, setQ] = useState("");
  const [actionType, setActionType] = useState("");
  const [rows, setRows] = useState<any[]>([]);
  const [knownActionTypes, setKnownActionTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [confirmTarget, setConfirmTarget] = useState<{ mode: RevertMode; row: any } | null>(null);
  const [confirmText, setConfirmText] = useState("");
  const [actorName, setActorName] = useState(enteredName);
  const [confirmError, setConfirmError] = useState("");
  const [busy, setBusy] = useState(false);
  const [backupBusy, setBackupBusy] = useState(false);
  const [backupError, setBackupError] = useState("");
  const [backupSummary, setBackupSummary] = useState("");
  const [showBackupImport, setShowBackupImport] = useState(false);
  const [backupFile, setBackupFile] = useState<File | null>(null);
  const [backupConfirmText, setBackupConfirmText] = useState("");
  const [backupActorName, setBackupActorName] = useState(enteredName);
  const requestIdRef = useRef(0);
  const requestAbortRef = useRef<AbortController | null>(null);
  const debouncedQuery = useDebounced(q);

  const refresh = async ({
    throwOnFailure = false,
    append = false
  }: {
    throwOnFailure?: boolean;
    append?: boolean;
    } = {}) => {
    if (append && !nextCursor) return;
    const requestId = ++requestIdRef.current;
    requestAbortRef.current?.abort();
    const controller = new AbortController();
    requestAbortRef.current = controller;
    if (append) {
      setLoadingMore(true);
    } else if (rows.length === 0) {
      setLoading(true);
    } else {
      setUpdating(true);
    }
    setError("");
    try {
      const result = await api.getChangeLogHistory(
        debouncedQuery,
        actionType,
        PAGE_SIZE,
        append ? nextCursor ?? "" : "",
        {
          signal: controller.signal,
          dedupe: false
        }
      );
      if (requestId !== requestIdRef.current) return;
      const entries = result.entries ?? [];
      setKnownActionTypes((current) =>
        Array.from(
          new Set([
            ...current,
            ...(result.actionTypes ?? []),
            ...entries.map((row: any) => row.actionType).filter(Boolean)
          ])
        ).sort((left, right) => left.localeCompare(right))
      );
      setRows((current) => {
        if (!append) return entries;
        const knownIds = new Set(current.map((row) => row.id));
        return [...current, ...entries.filter((row: any) => !knownIds.has(row.id))];
      });
      setNextCursor(result.nextCursor ?? null);
    } catch (err: any) {
      if (requestId !== requestIdRef.current) return;
      setError(err?.message ?? "Failed to load changelog");
      if (throwOnFailure) throw err;
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setUpdating(false);
        setLoadingMore(false);
      }
    }
  };

  useEffect(() => {
    if (q === debouncedQuery) return;
    requestIdRef.current += 1;
    requestAbortRef.current?.abort();
    setUpdating(false);
    setLoadingMore(false);
  }, [q, debouncedQuery]);

  useEffect(() => {
    setNextCursor(null);
    refresh().catch(() => undefined);
    return () => requestAbortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, actionType]);

  useEffect(() => {
    setActorName(enteredName);
    setBackupActorName(enteredName);
  }, [enteredName]);

  const actionTypeOptions = useMemo(() => knownActionTypes, [knownActionTypes]);
  const editorOpen = Boolean(confirmTarget || showBackupImport);
  useUnsavedChangesWarning(editorOpen);
  useRegisterViewRefresh(() => refresh({ throwOnFailure: true }), {
    disabled: loading || updating || loadingMore || busy || backupBusy || editorOpen,
    disabledReason: editorOpen ? "Finish or close the open confirmation before refreshing." : "The change log is busy."
  });

  const openConfirm = (mode: RevertMode, row: any) => {
    setNotice("");
    setConfirmError("");
    setConfirmText("");
    setConfirmTarget({ mode, row });
  };

  const executeRevert = async () => {
    if (!confirmTarget) return;
    setConfirmError("");
    setNotice("");

    const name = actorName.trim();
    if (!name) {
      setConfirmError("Your Name is required.");
      return;
    }
    if (confirmText.trim() !== "I understand") {
      setConfirmError("Type 'I understand' to continue.");
      return;
    }

    setBusy(true);
    try {
      const result = await api.revertChangeLog({
        mode: confirmTarget.mode,
        transactionId: confirmTarget.row.id,
        enteredName: name,
        confirmationText: confirmText
      });
      rememberName(name);
      setConfirmTarget(null);
      const successMessage =
        confirmTarget.mode === "single"
          ? `Restored single change. Reverted ${result.revertedCount} change.`
          : `Reverted ${result.revertedCount} changes to selected point.`;
      setNotice(successMessage);
      try {
        await refresh({ throwOnFailure: true });
      } catch {
        setNotice(`${successMessage} Latest log data could not be refreshed; use Refresh when the connection improves.`);
      }
    } catch (err: any) {
      setConfirmError(err?.message ?? "Failed to apply revert.");
    } finally {
      setBusy(false);
    }
  };

  const exportBackup = async () => {
    setBackupBusy(true);
    setBackupError("");
    setBackupSummary("");
    setNotice("");
    try {
      const response = await fetch("/api/backup/export", {
        method: "GET",
        headers: {
          "cache-control": "no-store"
        }
      });
      if (!response.ok) {
        const payload: any = await response.json().catch(() => null);
        throw new Error(payload?.error?.message ?? "Failed to export backup CSV.");
      }
      const blob = await response.blob();
      const contentDisposition = response.headers.get("content-disposition") ?? "";
      const filenameMatch = /filename="([^"]+)"/i.exec(contentDisposition);
      const filename = filenameMatch?.[1] ?? `qrs-system-backup-${new Date().toISOString().slice(0, 19)}.csv`;

      const link = document.createElement("a");
      const objectUrl = URL.createObjectURL(blob);
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
      setBackupSummary(`Backup exported: ${filename}`);
    } catch (err: any) {
      setBackupError(err?.message ?? "Failed to export backup CSV.");
    } finally {
      setBackupBusy(false);
    }
  };

  const openBackupImport = () => {
    setBackupError("");
    setBackupSummary("");
    setBackupConfirmText("");
    setBackupFile(null);
    setBackupActorName(enteredName);
    setShowBackupImport(true);
  };

  const executeBackupImport = async () => {
    setBackupError("");
    setBackupSummary("");
    setNotice("");

    const name = backupActorName.trim();
    if (!backupFile) {
      setBackupError("Choose a backup CSV file.");
      return;
    }
    if (!name) {
      setBackupError("Your Name is required.");
      return;
    }
    if (backupConfirmText.trim() !== "I understand") {
      setBackupError("Type 'I understand' to continue.");
      return;
    }

    setBackupBusy(true);
    try {
      const csv = await backupFile.text();
      const result = await api.importSystemBackup({
        csv,
        enteredName: name,
        confirmationText: backupConfirmText
      });
      rememberName(name);
      setShowBackupImport(false);
      setBackupFile(null);
      setBackupConfirmText("");
      const nonZeroCounts = Object.entries(result.importedCounts ?? {})
        .filter(([, count]) => Number(count) > 0)
        .map(([table, count]) => `${table}: ${count}`);
      setBackupSummary(
        `Backup imported (${result.totalRows} rows). ${nonZeroCounts.length > 0 ? nonZeroCounts.join(" · ") : ""}`.trim()
      );
      setNotice("System backup import completed.");
      try {
        await refresh({ throwOnFailure: true });
      } catch {
        setNotice("System backup import completed. Latest log data could not be refreshed; use Refresh when the connection improves.");
      }
    } catch (err: any) {
      setBackupError(err?.message ?? "Failed to import backup CSV.");
    } finally {
      setBackupBusy(false);
    }
  };

  return (
    <div className="stack-md">
      {error ? <p className="error-text">{error}</p> : null}
      {notice ? <p className="muted small">{notice}</p> : null}
      {backupSummary ? <p className="muted small">{backupSummary}</p> : null}
      {backupError ? <p className="error-text">{backupError}</p> : null}

      <section className="card stack-sm">
        <div className="row-between">
          <h2>System Backup</h2>
        </div>
        <p className="muted small">
          Export a full CSV snapshot of the system, or import a prior CSV snapshot to fully restore data.
        </p>
        <div className="actions-wrap">
          <button type="button" className="btn btn-secondary" onClick={exportBackup} disabled={backupBusy}>
            Export Backup CSV
          </button>
          <button type="button" className="btn btn-danger" onClick={openBackupImport} disabled={backupBusy}>
            Import Backup CSV
          </button>
        </div>
      </section>

      <section className="card stack-sm">
        <div className="row-between">
          <h2>Change Log</h2>
          <p className="muted small" role="status" aria-live="polite">
            {rows.length} {nextCursor ? "entries shown" : "entries"}
            {updating ? " · Updating…" : ""}
          </p>
        </div>
        <div className="stock-toolbar">
          <label className="stack-xs">
            <span className="label">Search</span>
            <input className="input" placeholder="Search item, person, note" value={q} onChange={(event) => setQ(event.target.value)} />
          </label>
          <label className="stack-xs">
            <span className="label">Action Type</span>
            <select className="input" value={actionType} onChange={(event) => setActionType(event.target.value)}>
              <option value="">All actions</option>
              {actionTypeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      <section className="card stack-sm">
        {loading && rows.length === 0 ? (
          <p className="muted">Loading changelog…</p>
        ) : (
          <>
            <div className="stock-table-wrap" aria-busy={updating || loadingMore}>
              <table className="stock-table changelog-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Change #</th>
                    <th>Who</th>
                    <th>Action</th>
                    <th>Item</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Qty</th>
                    <th>Bin</th>
                    <th>Notes</th>
                    <th>Revert</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                  const alreadyReverted = Boolean(row.revertedAt);
                  const generatedRevert = Boolean(row.revertSourceTransactionId);
                  const singleRevertUnavailable = row.singleRevertable === false;
                  return (
                    <tr key={row.id}>
                      <td>
                        <strong>{new Date(row.createdAt).toLocaleString()}</strong>
                      </td>
                      <td>#{row.sequence}</td>
                      <td>{row.enteredName}</td>
                      <td>
                        <div className="stack-xs">
                          <span>{row.actionType}</span>
                          {generatedRevert ? <span className="reason-chip reason-chip-neutral">Revert event</span> : null}
                          {alreadyReverted ? <span className="reason-chip reason-chip-yellow">Reverted</span> : null}
                        </div>
                      </td>
                      <td>{row.itemName}</td>
                      <td>{formatLocation(row, "from")}</td>
                      <td>{formatLocation(row, "to")}</td>
                      <td>{formatQty(row)}</td>
                      <td>{formatBin(row)}</td>
                      <td className="text-wrap-anywhere">{row.note ?? "—"}</td>
                      <td>
                        <div className="actions-wrap stock-row-actions">
                          <button
                            className="btn btn-secondary"
                            onClick={() => openConfirm("single", row)}
                            disabled={alreadyReverted || generatedRevert || singleRevertUnavailable}
                            title={row.singleRevertBlockReason ?? undefined}
                          >
                            Restore Change
                          </button>
                          <button
                            className="btn btn-danger"
                            onClick={() => openConfirm("to_point", row)}
                            disabled={alreadyReverted || generatedRevert}
                            title={alreadyReverted ? "A reverted change cannot be used as a revert point." : undefined}
                          >
                            Revert To Here
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                  })}
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="muted small">
                        No changelog entries match these filters.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            {nextCursor ? (
              <div className="actions-wrap">
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={loadingMore || updating}
                  onClick={() => void refresh({ append: true })}
                >
                  {loadingMore ? "Loading…" : "Load more"}
                </button>
              </div>
            ) : null}
          </>
        )}
      </section>

      {confirmTarget ? (
        <div className="modal-backdrop" onClick={() => (busy ? undefined : setConfirmTarget(null))}>
          <div className="modal-card move-sheet-card" onClick={(event) => event.stopPropagation()}>
            <ModalCloseButton onClick={() => setConfirmTarget(null)} disabled={busy} />
            <h3>{confirmTarget.mode === "single" ? "Restore Single Change" : "Revert To This Point"}</h3>
            <p className="muted small">
              {confirmTarget.mode === "single"
                ? "This restores only the selected change."
                : "This reverts all changes after the selected log entry."}
            </p>
            <div className="card-soft stack-xs">
              <p>
                <strong>{confirmTarget.row.itemName}</strong> · {confirmTarget.row.actionType}
              </p>
              <p className="muted small">
                {new Date(confirmTarget.row.createdAt).toLocaleString()} · {confirmTarget.row.enteredName}
              </p>
            </div>
            <label className="stack-xs" style={{ marginTop: "0.75rem" }}>
              <span className="label">Your Name</span>
              <input className="input" value={actorName} onChange={(event) => setActorName(event.target.value)} />
            </label>
            <label className="stack-xs">
              <span className="label">Type exactly: I understand</span>
              <input className="input" value={confirmText} onChange={(event) => setConfirmText(event.target.value)} />
            </label>
            {confirmError ? <p className="error-text small">{confirmError}</p> : null}
            <div className="actions-wrap" style={{ marginTop: "0.75rem" }}>
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmTarget(null)} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="btn btn-danger" onClick={executeRevert} disabled={busy}>
                {confirmTarget.mode === "single" ? "Restore Change" : "Revert To Point"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showBackupImport ? (
        <div className="modal-backdrop" onClick={() => (backupBusy ? undefined : setShowBackupImport(false))}>
          <div className="modal-card move-sheet-card" onClick={(event) => event.stopPropagation()}>
            <ModalCloseButton onClick={() => setShowBackupImport(false)} disabled={backupBusy} />
            <h3>Import System Backup CSV</h3>
            <p className="muted small">
              This is destructive. Current data will be replaced with the backup file.
            </p>
            <label className="stack-xs" style={{ marginTop: "0.75rem" }}>
              <span className="label">Backup CSV File</span>
              <input
                className="input"
                type="file"
                accept=".csv,text/csv"
                onChange={(event) => setBackupFile(event.target.files?.[0] ?? null)}
              />
            </label>
            <label className="stack-xs">
              <span className="label">Your Name</span>
              <input className="input" value={backupActorName} onChange={(event) => setBackupActorName(event.target.value)} />
            </label>
            <label className="stack-xs">
              <span className="label">Type exactly: I understand</span>
              <input className="input" value={backupConfirmText} onChange={(event) => setBackupConfirmText(event.target.value)} />
            </label>
            {backupError ? <p className="error-text small">{backupError}</p> : null}
            <div className="actions-wrap" style={{ marginTop: "0.75rem" }}>
              <button type="button" className="btn btn-secondary" onClick={() => setShowBackupImport(false)} disabled={backupBusy}>
                Cancel
              </button>
              <button type="button" className="btn btn-danger" onClick={executeBackupImport} disabled={backupBusy}>
                Import and Replace Data
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
